/** /preflight-templates — system-wide preflight item templates (grid + CRUD dialog).
 *
 *  Templates are the tour_operator=null blueprint rows the per-TO checklist is
 *  materialized from. Filterable by group + a free-text search. */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { groups, templates, type PreflightTaskType, type PreflightTemplate } from '@/api/preflight';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { useGridFilters, type FilterDef } from '@/hooks/useGridFilters';
import { useCreateIntent } from '@/hooks/useCreateIntent';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { PreflightTemplateDialog } from './PreflightTemplateDialog';

const TASK_TYPE_COLOR: Record<PreflightTaskType, 'default' | 'info' | 'secondary'> = {
  manual: 'default',
  async: 'info',
  multistep: 'secondary',
};

const digits = (v: string) => (/^\d+$/.test(v) ? Number(v) : undefined);

const FILTERS: FilterDef[] = [{ param: 'group', toApi: digits }, { param: 'active' }];

export function PreflightTemplatesListPage() {
  const { hasPerm } = usePermissions();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const canManage = hasPerm('console_preflight_manage');

  const [editing, setEditing] = useState<PreflightTemplate | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState<PreflightTemplate | null>(null);

  useCreateIntent(() => openCreate());

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = useCallback((t: PreflightTemplate) => {
    setEditing(t);
    setDialogOpen(true);
  }, []);

  const {
    extra,
    getParam,
    setParam,
    resetAll: resetAllFilters,
    anyActive: anyFilterActive,
  } = useGridFilters(FILTERS);

  const groupParam = getParam('group') ?? '';
  const activeParam = getParam('active') ?? '';

  // Group options for the filter Select.
  const groupsQuery = useQuery({
    queryKey: ['preflight', 'groups', 'all'],
    queryFn: () => groups.list({ pageSize: 200, ordering: 'sequence' }),
    staleTime: 60_000,
  });
  const groupOptions = groupsQuery.data?.results ?? [];

  const deleteMutation = useMutation({
    mutationFn: (id: number) => templates.remove(id),
    onSuccess: () => {
      snackbar.show('Template deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['preflight', 'templates'] });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error'),
  });

  const columns = useMemo<GridColDef<PreflightTemplate>[]>(
    () => [
      {
        field: 'group_name',
        headerName: 'Group',
        width: 150,
        sortable: false,
        valueGetter: (_, row) => row.group_name || '—',
      },
      { field: 'action', headerName: 'Action', width: 200 },
      { field: 'title', headerName: 'Title', flex: 1, minWidth: 220 },
      {
        field: 'task_type',
        headerName: 'Task type',
        width: 130,
        sortable: false,
        renderCell: (p) => (
          <Chip
            size="small"
            variant="outlined"
            color={TASK_TYPE_COLOR[p.row.task_type]}
            label={p.row.task_type}
          />
        ),
      },
      {
        field: 'owner_role',
        headerName: 'Owner',
        width: 110,
        sortable: false,
        valueGetter: (_, row) => row.owner_role || '—',
      },
      {
        field: 'steps',
        headerName: 'Steps',
        width: 90,
        sortable: false,
        valueGetter: (_, row) => row.steps.length,
      },
      {
        field: 'optional',
        headerName: 'Optional',
        width: 100,
        type: 'boolean',
        sortable: false,
        valueGetter: (_, row) => row.optional,
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 90,
        type: 'boolean',
        sortable: false,
        valueGetter: (_, row) => row.active,
      },
      {
        field: 'can_auto_run',
        headerName: 'Auto-run',
        width: 110,
        sortable: false,
        renderCell: (p) =>
          p.row.can_auto_run ? (
            <Chip size="small" color="success" variant="outlined" label="auto" />
          ) : (
            <Chip size="small" variant="outlined" label="manual" />
          ),
      },
      ...(canManage
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 96,
              sortable: false,
              filterable: false,
              renderCell: (p: { row: PreflightTemplate }) => (
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(p.row);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        setToDelete(p.row);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ),
            } as GridColDef<PreflightTemplate>,
          ]
        : []),
    ],
    [canManage, openEdit],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      templates.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? 'sequence',
        search: params.search,
        ...(params.extra as Record<string, never>),
      }),
    [],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'group',
      priority: 5,
      active: groupParam !== '',
      node: (
        <TextField
          select
          size="small"
          label="Group"
          value={groupParam}
          sx={{ width: 200 }}
          onChange={(e) => setParam('group', e.target.value || null)}
        >
          <MenuItem value="">All groups</MenuItem>
          {groupOptions.map((g) => (
            <MenuItem key={g.id} value={String(g.id)}>
              {g.name}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      key: 'active',
      priority: 4,
      active: activeParam !== '',
      node: (
        <TextField
          select
          size="small"
          label="Active"
          value={activeParam}
          sx={{ width: 140 }}
          onChange={(e) => setParam('active', e.target.value || null)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Active</MenuItem>
          <MenuItem value="false">Inactive</MenuItem>
        </TextField>
      ),
    },
  ];

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="preflight-concept"
        title="Preflight Templates"
        subtitle="System-wide blueprint rows the per-operator onboarding checklist is generated from."
        actions={
          canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create template
            </Button>
          )
        }
      />

      <PreflightTemplateDialog
        open={dialogOpen}
        template={editing}
        onClose={() => setDialogOpen(false)}
      />

      <ResponsiveFilterBar items={toolbarItems} />

      <ResourceDataGrid<PreflightTemplate>
        queryKey={['preflight', 'templates']}
        queryFn={queryFn}
        columns={columns}
        extra={extra}
        defaultSort={{ field: 'sequence', sort: 'asc' }}
        emptyMessage="No templates yet."
        emptyAction={
          canManage ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Create template
            </Button>
          ) : undefined
        }
        headerToolbar={{
          onResetAllFilters: resetAllFilters,
          resetDisabled: !anyFilterActive,
          search: true,
          searchPlaceholder: 'Action or title',
          exportFileName: 'preflight-templates',
        }}
      />

      <ConfirmDialog
        open={toDelete !== null}
        title="Delete this template?"
        message={
          toDelete
            ? `"${toDelete.title}" (${toDelete.action}) will be deleted. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete template"
        destructive
        onConfirm={() => {
          if (toDelete) deleteMutation.mutate(toDelete.id);
          setToDelete(null);
        }}
        onCancel={() => setToDelete(null)}
      />
    </Box>
  );
}
